import type { ReactNode } from "react";
import { AdBottomLeaderboard } from "components/modules/Ads/AdBottomLeaderboard";
import { AdTopLeaderboard } from "components/modules/Ads/AdTopLeaderboard";
import { useFixedEditorLayout } from "./useFixedEditorLayout";
import { BetaBanner } from "components/common/BetaBanner/BetaBanner";
import { Translate } from "components/common/Translate/Translate";
import AdsLogicWrapper from "../../Ads/AdsLogicWrapper";

interface FixedEditorLayoutProps {
    leftBar: ReactNode;
    controlBar?: ReactNode;
    preview: ReactNode;
    bottomMenu: ReactNode;
    action?: {
        desktop: ReactNode;
    };
    navbar: ReactNode;
    config?: Config;
}

interface Config {
    hideTopAd?: boolean;
    hideBottomAd?: boolean;
    showBanner?: boolean;
    disableShadow?: boolean;
    leftBar?: {
        minWidth?: string;
        width?: string;
        maxWidth?: string;
    };
}

export const FixedEditorLayout = ({
    leftBar,
    controlBar,
    preview,
    bottomMenu,
    navbar,
    action,
    config,
}: FixedEditorLayoutProps) => {
    const { width, bottomMenuRef, isLandscape } = useFixedEditorLayout();

    return (
        <>
            <div
                className={`max-w-1440 ${!config?.disableShadow && `page-shadow`} max-h-screen h-screen flex flex-col`}
            >
                <div className="z-20 flex-grow-0 flex-shrink-0">{navbar}</div>
                <div className="flex flex-grow w-full h-full min-h-0 overflow-x-auto overflow-y-hidden">
                    <div
                        className="hidden h-full text-black bg-white border-r lg:block border-r-irGray-200 dark:border-r-darkSurface-600 dark:bg-darkSurface-100 dark:text-white"
                        style={{ minWidth: "250px", width: "360px", maxWidth: "360px", ...(config?.leftBar ?? {}) }}
                    >
                        {leftBar}
                    </div>
                    <div className="flex-grow flex flex-col h-full min-w-728-pc w-full min-h-0 max-h-full overflow-y-hidden gap-2.5 dark:bg-darkSurface-100">
                        {action && controlBar && (
                            <div className="sticky top-0 z-10 items-center hidden w-full bg-white lg:flex">
                                <div className="flex items-center justify-between w-full border-b border-b-irGray-200 dark:border-b-darkSurface-600">
                                    <div className="w-full">{controlBar}</div>
                                    {action.desktop && <div className="mr-4">{action.desktop}</div>}
                                </div>
                            </div>
                        )}
                        <AdsLogicWrapper placeHolder={<div></div>}>
                            {!config?.hideTopAd && !isLandscape && !config?.showBanner && (
                                <div className="py-[5px] border-b border-b-irGray-200 dark:border-b-darkSurface-600 relative">
                                    <div className="flex-grow-0 flex-shrink-0 flex items-center justify-center mx-auto w-[320px] md:w-[728px] h-[50px] md:h-[90px]">
                                        <AdTopLeaderboard />
                                    </div>
                                    <div className="absolute right-0 top-0 text-xs capitalize bg-irGray-200 dark:bg-darkSurface-300 dark:text-white py-0.5 px-1">
                                        <Translate keyName="common:advertisement" />
                                    </div>
                                </div>
                            )}
                        </AdsLogicWrapper>
                        {config?.showBanner && (
                            <div className="flex items-center justify-center py-4 mx-4 lg:py-2">
                                <BetaBanner />
                            </div>
                        )}
                        <div className="flex-grow w-full min-h-0 scroll">{preview}</div>
                        <AdsLogicWrapper>
                            {!config?.hideBottomAd && !isLandscape && (
                                <div className="py-[5px] border-t border-t-irGray-200 dark:border-t-darkSurface-600 relative">
                                    <div className="flex-grow-0 flex-shrink-0 lg:flex items-center justify-center mx-auto w-[320px] md:w-[728px] h-[50px] md:h-[90px]">
                                        <AdBottomLeaderboard />
                                    </div>
                                    <div className="absolute right-0 top-0 text-xs capitalize bg-irGray-200 dark:bg-darkSurface-300 dark:text-white py-0.5 px-1">
                                        <Translate keyName="common:advertisement" />
                                    </div>
                                </div>
                            )}
                        </AdsLogicWrapper>
                        <AdsLogicWrapper>
                            {width < 1024 ? (
                                <div
                                    className="flex-grow-0 flex-shrink-0 w-full"
                                    style={{
                                        minHeight: bottomMenuRef.current
                                            ? `${bottomMenuRef.current.getBoundingClientRect().height}px`
                                            : "78px",
                                    }}
                                ></div>
                            ) : (
                                <div></div>
                            )}
                        </AdsLogicWrapper>
                    </div>
                </div>

                <div className="fixed inset-x-0 bottom-0 z-30 block lg:hidden" ref={bottomMenuRef}>
                    {bottomMenu}
                </div>
            </div>
            <div id="placeholder" />
        </>
    );
};
